import { useEffect, useState } from 'react';
import { DEFAULT_LOCALE, type Locale } from '@superposition/domain';
import * as api from './api.js';

/**
 * профиль (имя, пол, время вопроса) нужен нескольким экранам: колесу для
 * заголовка, наблюдателю для обращений, настройкам. грузим один раз,
 * после сохранения настроек обновляются все.
 */

let cached: api.ProfileSettings | null = null;
let loading: Promise<void> | null = null;
const listeners = new Set<(p: api.ProfileSettings) => void>();

function publish(p: api.ProfileSettings): void {
  cached = p;
  for (const l of listeners) l(p);
}

export function setProfile(p: api.ProfileSettings): void {
  publish(p);
}

export function useProfile(): api.ProfileSettings | null {
  const [profile, setLocal] = useState(cached);
  useEffect(() => {
    listeners.add(setLocal);
    if (!cached && !loading) {
      loading = api
        .fetchProfile()
        .then(publish)
        .catch(() => {
          // без профиля экраны работают с нейтральными словами
        })
        .finally(() => {
          loading = null;
        });
    }
    return () => {
      listeners.delete(setLocal);
    };
  }, []);
  return profile;
}

/**
 * язык без загрузки профиля: экраны до входа и до ответа сервера видят
 * русский, после загрузки профиля перерисовываются на его языке
 */
export function useLocale(): Locale {
  const [locale, setLocale] = useState<Locale>(cached?.locale ?? DEFAULT_LOCALE);
  useEffect(() => {
    const l = (p: api.ProfileSettings): void => setLocale(p.locale ?? DEFAULT_LOCALE);
    listeners.add(l);
    if (cached) l(cached);
    return () => {
      listeners.delete(l);
    };
  }, []);
  return locale;
}

// тот же прием для списка практик озера: он общий у озера и наблюдателя.
// записи у каждого языка свои, поэтому список хранится по языку
const lakeCached = new Map<Locale, api.LakePractice[]>();
const lakeLoading = new Map<Locale, Promise<void>>();
const lakeListeners = new Set<(locale: Locale, p: api.LakePractice[]) => void>();

export function useLakePractices(): api.LakePractice[] | null {
  const locale = useLocale();
  const [practices, setLocal] = useState<api.LakePractice[] | null>(lakeCached.get(locale) ?? null);
  useEffect(() => {
    setLocal(lakeCached.get(locale) ?? null);
    const l = (loc: Locale, p: api.LakePractice[]): void => {
      if (loc === locale) setLocal(p);
    };
    lakeListeners.add(l);
    if (!lakeCached.has(locale) && !lakeLoading.has(locale)) {
      lakeLoading.set(
        locale,
        api
          .fetchLake()
          .then((r) => {
            lakeCached.set(locale, r.practices);
            for (const fn of lakeListeners) fn(locale, r.practices);
          })
          .catch(() => {
            // без списка озеро работает с тоном
          })
          .finally(() => {
            lakeLoading.delete(locale);
          }),
      );
    }
    return () => {
      lakeListeners.delete(l);
    };
  }, [locale]);
  return practices;
}
